import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import ExportButton from '../../utils/ExportButton';
import '../../styles/AuditTrailDashboard.css';


// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};
const AuditTrailDashboard = () => {
  // State management
  const [auditData, setAuditData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    requester: '',
    approver: '',
    actionType: '',
    status: ''
  });

  // Available filter options
  const actionTypes = [
    'status_change',
    'data_edit',
    'data_delete',
    'permission_change',
    'other'
  ];

  const statusOptions = ['approved', 'rejected', 'pending'];

  // Load audit trail data
  useEffect(() => {
    fetchAuditData();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [auditData, filters]);

  // Fetch audit trail data
  const fetchAuditData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching audit trail from:', `${API_BASE_URL}/audit_trail.php`);
      
      const response = await axios.get(
        `${API_BASE_URL}/audit_trail.php`,
        getAuthConfig()
      );

      console.log('Audit trail response:', response.data);

      if (response.data.success) {
        setAuditData(response.data.audit_trail || []);
        if (response.data.audit_trail.length === 0) {
          setError('No audit trail records found. The database table might be empty.');
        }
      } else {
        setError(response.data.message || 'Failed to load audit trail');
      }
    } catch (err) {
      console.error('Error fetching audit trail:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
      setError('Failed to fetch audit trail: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to data
  const applyFilters = () => {
    let filtered = [...auditData];

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(item => 
        new Date(item.created_at) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => 
        new Date(item.created_at) <= endDate
      );
    }

    // Filter by requester
    if (filters.requester) {
      filtered = filtered.filter(item =>
        item.requester_name?.toLowerCase().includes(filters.requester.toLowerCase())
      );
    }

    // Filter by approver
    if (filters.approver) {
      filtered = filtered.filter(item =>
        item.approver_name?.toLowerCase().includes(filters.approver.toLowerCase())
      );
    }

    // Filter by action type
    if (filters.actionType) {
      filtered = filtered.filter(item => item.action_type === filters.actionType);
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    setFilteredData(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      requester: '',
      approver: '',
      actionType: '',
      status: ''
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const statusClasses = {
      approved: 'status-approved',
      rejected: 'status-rejected',
      pending: 'status-pending'
    };
    return statusClasses[status] || 'status-unknown';
  };

  // Get action type label
  const getActionTypeLabel = (actionType) => {
    const labels = {
      status_change: 'Status Change',
      data_edit: 'Data Edit',
      data_delete: 'Data Delete',
      permission_change: 'Permission Change',
      other: 'Other'
    };
    return labels[actionType] || actionType;
  };

  // Export data with current filters
  const handleExport = () => {
    // This will be handled by ExportButton component
    // which will use the filteredData
  };

  // View details modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="audit-trail-container">
        <div className="loading-spinner">Loading audit trail...</div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="audit-trail-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchAuditData} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="audit-trail-container">
      <div className="header-section">
        <h2>Audit Trail</h2>
        <div className="header-actions">
          <button className="btn-secondary" onClick={fetchAuditData}>
            🔄 Refresh
          </button>
          <ExportButton 
            data={filteredData}
            filename={`audit_trail_${new Date().toISOString().split('T')[0]}`}
            type="audit_trail"
          />
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>Requester</label>
            <input
              type="text"
              name="requester"
              placeholder="Search by requester name"
              value={filters.requester}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>Approver</label>
            <input
              type="text"
              name="approver"
              placeholder="Search by approver name"
              value={filters.approver}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>Action Type</label>
            <select
              name="actionType"
              value={filters.actionType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              {actionTypes.map(type => (
                <option key={type} value={type}>
                  {getActionTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-secondary" onClick={handleClearFilters}>
            Clear Filters
          </button>
          <span className="results-count">
            Showing {filteredData.length} of {auditData.length} records
          </span>
        </div>
      </div>

      {/* Audit Trail Table */}
      <div className="table-container">
        {filteredData.length === 0 ? (
          <div className="no-data">
            <p>No audit trail records found.</p>
            {Object.values(filters).some(f => f !== '') && (
              <p>Try adjusting your filters.</p>
            )}
          </div>
        ) : (
          <table className="audit-trail-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date & Time</th>
                <th>Action Type</th>
                <th>Description</th>
                <th>Requester</th>
                <th>Approver</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{formatDate(item.created_at)}</td>
                  <td>
                    <span className="action-type-badge">
                      {getActionTypeLabel(item.action_type)}
                    </span>
                  </td>
                  <td className="description-cell">
                    {item.description || 'N/A'}
                  </td>
                  <td>{item.requester_name || 'Unknown'}</td>
                  <td>{item.approver_name || 'Pending'}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(item.status)}`}>
                      {item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-action btn-view"
                      onClick={() => handleViewDetails(item)}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content details-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Audit Trail Details</h3>
              <button className="btn-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item">
                  <label>ID:</label>
                  <span>{selectedItem.id}</span>
                </div>

                <div className="detail-item">
                  <label>Date & Time:</label>
                  <span>{formatDate(selectedItem.created_at)}</span>
                </div>

                <div className="detail-item">
                  <label>Action Type:</label>
                  <span className="action-type-badge">
                    {getActionTypeLabel(selectedItem.action_type)}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusBadge(selectedItem.status)}`}>
                    {selectedItem.status?.charAt(0).toUpperCase() + selectedItem.status?.slice(1)}
                  </span>
                </div>

                <div className="detail-item full-width">
                  <label>Description:</label>
                  <p>{selectedItem.description || 'No description provided'}</p>
                </div>

                <div className="detail-item full-width">
                  <label>Reason:</label>
                  <p>{selectedItem.reason || 'No reason provided'}</p>
                </div>

                <div className="detail-item">
                  <label>Requester:</label>
                  <span>{selectedItem.requester_name || 'Unknown'}</span>
                </div>

                <div className="detail-item">
                  <label>Requester ID:</label>
                  <span>{selectedItem.requester_id || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Approver:</label>
                  <span>{selectedItem.approver_name || 'Not yet approved'}</span>
                </div>

                <div className="detail-item">
                  <label>Approver ID:</label>
                  <span>{selectedItem.approver_id || 'N/A'}</span>
                </div>

                {selectedItem.approved_at && (
                  <div className="detail-item">
                    <label>Approved At:</label>
                    <span>{formatDate(selectedItem.approved_at)}</span>
                  </div>
                )}

                {selectedItem.approval_comments && (
                  <div className="detail-item full-width">
                    <label>Approval Comments:</label>
                    <p>{selectedItem.approval_comments}</p>
                  </div>
                )}

                {selectedItem.related_id && (
                  <div className="detail-item">
                    <label>Related Record ID:</label>
                    <span>{selectedItem.related_id}</span>
                  </div>
                )}

                {selectedItem.related_type && (
                  <div className="detail-item">
                    <label>Related Type:</label>
                    <span>{selectedItem.related_type}</span>
                  </div>
                )}

                {selectedItem.ip_address && (
                  <div className="detail-item">
                    <label>IP Address:</label>
                    <span>{selectedItem.ip_address}</span>
                  </div>
                )}

                {/* Changes Section */}
                {(selectedItem.old_values || selectedItem.new_values) && (
                  <div className="detail-item full-width">
                    <label style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px', display: 'block' }}>
                      Changes Details:
                    </label>
                  </div>
                )}

                {selectedItem.old_values && (
                  <div className="detail-item full-width">
                    <label style={{ color: '#dc2626', fontWeight: '600' }}>Old Values:</label>
                    <pre style={{ 
                      background: '#fef2f2', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      border: '1px solid #fecaca',
                      fontSize: '13px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(selectedItem.old_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedItem.new_values && (
                  <div className="detail-item full-width">
                    <label style={{ color: '#10b981', fontWeight: '600' }}>New Values:</label>
                    <pre style={{ 
                      background: '#f0fdf4', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      border: '1px solid #bbf7d0',
                      fontSize: '13px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(selectedItem.new_values, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="statistics-section">
        <h3>Summary Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Records</div>
            <div className="stat-value">{filteredData.length}</div>
          </div>

          <div className="stat-card stat-approved">
            <div className="stat-label">Approved</div>
            <div className="stat-value">
              {filteredData.filter(item => item.status === 'approved').length}
            </div>
          </div>

          <div className="stat-card stat-rejected">
            <div className="stat-label">Rejected</div>
            <div className="stat-value">
              {filteredData.filter(item => item.status === 'rejected').length}
            </div>
          </div>

          <div className="stat-card stat-pending">
            <div className="stat-label">Pending</div>
            <div className="stat-value">
              {filteredData.filter(item => item.status === 'pending').length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailDashboard;